import React, { useEffect, useMemo, useState } from 'react';

function AdminPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('succeeded');

  const fetchPayments = async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/admin/payments', {
        credentials: 'same-origin',
        headers: {
          'x-auth-token': token,
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch payments (endpoint may not be implemented)');
      }
      const data = await resp.json();
      // server returns an array of payment items
      setItems(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPayments(); }, []);

  const getStatus = (payment) => String(payment.status || payment.state || '').toLowerCase();
  const formatTimestamp = (value) => {
    if (!value) return '';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    const normalized = numeric > 10_000_000_000 ? numeric : numeric * 1000;
    return new Date(normalized).toLocaleString();
  };
  const succeededCount = useMemo(() => items.filter((p) => getStatus(p) === 'succeeded').length, [items]);
  const othersCount = useMemo(() => items.filter((p) => getStatus(p) !== 'succeeded').length, [items]);
  const filteredItems = useMemo(
    () => items.filter((p) => (activeFilter === 'succeeded' ? getStatus(p) === 'succeeded' : getStatus(p) !== 'succeeded')),
    [items, activeFilter]
  );

  return (
    <div className="page admin-payments-page">
      <header className="page-header">
        <span className="page-eyebrow">Admin</span>
        <h1>Payments</h1>
        <p className="page-subtitle">Track payment activity and confirm ad-removal purchases are recorded correctly.</p>
      </header>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="admin-actions">
        <button className="btn btn-outline-secondary" onClick={fetchPayments} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
        <button
          className={`btn ${activeFilter === 'succeeded' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setActiveFilter('succeeded')}
          disabled={loading}
        >
          Succeeded ({succeededCount})
        </button>
        <button
          className={`btn ${activeFilter === 'others' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setActiveFilter('others')}
          disabled={loading}
        >
          Others ({othersCount})
        </button>
      </div>

      <section className="panel admin-panel-surface mt-3">
        {loading ? <p>Loading payments…</p> : (
          filteredItems.length === 0 ? <div className="admin-empty">No payment items in this category.</div> : (
            <div className="table-responsive admin-table-wrap">
            <table className="table admin-table">
              <thead><tr><th>ID</th><th>User</th><th>Amount</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {filteredItems.map(p => (
                  <tr key={p.id || p._id}>
                    <td>{p.id || p._id}</td>
                    <td>{p.user ? (<a href={`/admin/users?selected=${p.user.id}`}>{p.user.email}</a>) : (p.metadata && p.metadata.userId) || p.customer || '—'}</td>
                    <td>{typeof p.amount === 'number' ? `$${(p.amount / 100).toFixed(2)}` : ''}</td>
                    <td><span className={`badge ${getStatus(p) === 'succeeded' ? 'bg-success' : 'bg-secondary'}`}>{p.status || p.state || ''}</span></td>
                    <td>{formatTimestamp(p.created || p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        )}
      </section>
    </div>
  );
}

export default AdminPayments;
